import React, { useState } from 'react';
import {
  Modal,
  Button,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
  InputGroup,
} from 'react-bootstrap';
import { PublicKey } from '@solana/web3.js';

const EMPTY_FORM = {
  reason: '',
  walletIds: [],
  solAmount: '',
  type: 'DEFAULT',
};

// Fail-closed base58 validation of every wallet in the parsed array.
const validateWalletIds = walletIds => {
  if (!Array.isArray(walletIds) || walletIds.length === 0) {
    return false;
  }
  return walletIds.every(w => {
    if (typeof w !== 'string') return false;
    try {
      // Throws on any non-base58 / wrong-length wallet.
      // eslint-disable-next-line no-new
      new PublicKey(w);
      return true;
    } catch (e) {
      return false;
    }
  });
};

const CreateRiskFreePlayModal = ({ isOpen, onClose, onCreate }) => {
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  // Controlled raw text for the textarea so the displayed value can never
  // diverge from the parsed walletIds in formData.
  const [walletIdsText, setWalletIdsText] = useState('');

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setWalletIdsText('');
  };

  // Reset on any dismissal (Create success, Cancel, or X) so a prior entry
  // can never carry over into the next open.
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Fail-closed but SILENT on every keystroke: parse the current text and
  // either commit a fully-valid array or DROP the target (walletIds: []).
  // A prior good paste can never survive a bad one. No alert here — blocking
  // dialogs on every character make the field unusable; user-facing errors
  // are surfaced once on blur and again on submit.
  const handleWalletIdsInput = e => {
    const raw = e.target.value;
    setWalletIdsText(raw);
    if (raw.trim() === '') {
      setFormData(prev => ({ ...prev, walletIds: [] }));
      return;
    }
    let walletIds;
    try {
      walletIds = JSON.parse(raw);
    } catch (error) {
      setFormData(prev => ({ ...prev, walletIds: [] }));
      return;
    }
    if (!validateWalletIds(walletIds)) {
      setFormData(prev => ({ ...prev, walletIds: [] }));
      return;
    }
    setFormData(prev => ({ ...prev, walletIds }));
  };

  // Blur is where we surface a single, non-blocking-per-keystroke error.
  // Uses the SAME validateWalletIds as onChange/submit so feedback is
  // consistent (an array of invalid base58 strings is flagged here too).
  const handleWalletIdsInputError = e => {
    const raw = e.target.value;
    if (raw.trim() === '') return;
    let walletIds;
    try {
      walletIds = JSON.parse(raw);
    } catch (error) {
      alert('Invalid JSON. Please provide a valid JSON array.');
      return;
    }
    if (!validateWalletIds(walletIds)) {
      alert('Invalid format. Please provide a non-empty array of valid wallet IDs.');
    }
  };

  const handleFileImport = e => {
    const file = e.target.files[0];
    if (file.type === 'application/json') {
      const reader = new FileReader();
      reader.onload = event => {
        try {
          const walletIds = JSON.parse(event.target.result);
          if (validateWalletIds(walletIds)) {
            setWalletIdsText(JSON.stringify(walletIds));
            setFormData(prev => ({ ...prev, walletIds }));
          } else {
            setWalletIdsText('');
            setFormData(prev => ({ ...prev, walletIds: [] }));
            alert('Invalid format. Please provide a non-empty array of valid wallet IDs.');
          }
        } catch (error) {
          setWalletIdsText('');
          setFormData(prev => ({ ...prev, walletIds: [] }));
          alert('Invalid JSON. Please provide a valid JSON array.');
        }
      };
      reader.readAsText(file);
    } else {
      alert('Invalid file type. Please upload a JSON file.');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    // Fail-closed guards: never POST a stale/empty target or a non-positive
    // amount. The Create button is always enabled, so validate here.
    if (!validateWalletIds(formData.walletIds)) {
      alert('Cannot create: no valid wallet IDs. Re-paste a valid JSON array.');
      return;
    }
    if (!(Number(formData.solAmount) > 0)) {
      alert('Cannot create: Sol amount must be greater than 0.');
      return;
    }
    await onCreate(formData);
    // Reset so the next open starts from a clean target — no stale carryover.
    resetForm();
    onClose();
  };

  return (
    <Modal show={isOpen} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Risk-Free Play</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <FormGroup>
            <FormLabel>Reason</FormLabel>
            <FormControl
              type="text"
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Sol Amount</FormLabel>
            <FormControl
              type="number"
              name="solAmount"
              value={formData.solAmount}
              onChange={handleChange}
              required
            />
          </FormGroup>
          {/* ... other input fields ... */}
          <FormGroup>
            <FormLabel>Wallet IDs</FormLabel>
            <FormControl
              as="textarea"
              name="walletIds"
              value={walletIdsText}
              placeholder='Paste wallet IDs as a JSON array, e.g. ["8UNuw6Wv8F47ymuCdZxzN29fK5ETafoyz2ih9YYAozVw", ...]'
              onBlur={handleWalletIdsInputError}
              onChange={handleWalletIdsInput}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Import Wallet IDs from JSON file</FormLabel>
            <InputGroup>
              <FormControl type="file" accept=".json" onChange={handleFileImport} />
            </InputGroup>
          </FormGroup>
          <Form.Group className="mb-3">
            <Form.Label>RFF Type</Form.Label>
            <div>
              <Form.Check
                type="radio"
                id="defaultRadio"
                label="Good For 1 Play (default)"
                name="type"
                value="DEFAULT"
                checked={formData.type === 'DEFAULT'}
                onChange={handleChange}
              />
              <Form.Check
                type="radio"
                id="goodTilWinRadio"
                label="Good Until Win"
                name="type"
                value="GOOD_TIL_WIN"
                checked={formData.type === 'GOOD_TIL_WIN'}
                onChange={handleChange}
              />
            </div>
          </Form.Group>
          {/* ... other input fields ... */}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" type="submit">
            Create
          </Button>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateRiskFreePlayModal;
